import React, {useState} from 'react';
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps} from "formik";
import {Exhibit, ExhibitData, ExhibitInformation, ExhibitInformationData} from "types/Exhibit";
import {Zone} from "types/Zone";
import IconName from "types/IconName";
import {ExhibitType} from "types/ExhibitType";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Field from "components/Form/Field";
import Selector from "components/Form/Selector";
import Locator from "components/Form/Locator";
import {InformationCircleIcon} from "@heroicons/react/outline";
import MultiSelector from "components/Form/MultiSelector";
import {Collection} from "types/Collection";
import Uploader from "components/Form/Uploader";
import Visibility from "types/Visibility";
import ExhibitPreview from "pages/Exhibit/Preview";
import {AnyMedia, MediaFile} from "types/MediaFile";
import FactType from "types/FactType";
import InformationListField from "components/Form/InformationEditor";
import Toggle from "components/Form/Toggle";
import FormControls from "components/FormControls";
import {useOperator} from "context/OperatorContext";
import {Element, ElementData} from "types/Element";
import Button from "components/Button";
import {ExclamationCircleIcon, PlusIcon} from "@heroicons/react/solid";
import AccessibilitySelector from "components/Form/AccessibilitySelector/AccessibilitySelector";
import ElementForm from "pages/Exhibit/Form/ElementForm";
import {toTitleCase} from "services/string";
import {Stream, StreamData, StreamProvider} from "types/Stream";
import LinkEditor from "components/Form/LinkEditor";
import {FactData} from "types/Fact";
import FactEditor from "./FactEditor";
import {useAuth} from "context/AuthContext";
import {TextAreaField, TextField} from "components/Form/Form";
import {SelectField} from "../../../components/Form/Form";
import {LinkOptions} from "../../../types/Link";
import {useLinkOptions} from "../../../hooks/useLinkOptions";
import {Geometry} from "geojson";
import {Building, BuildingData} from "../../../types/Building";
import FeatureFlag from "../../../types/FeatureFlag";
import {Floor, FloorData} from "../../../types/Floor";
import {OperatorSubdomain} from "../../../types/Operator";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {urlTest} from "../../../services/validation-schemas";
import {AIContent, ExhibitAIContentAPIData} from "../../../types/AIContent";
import {GageGenerateContent} from "../../../components/GageGenerateContent";
import {getAIContentStatus} from "../../../services/ai-content";

// Types ----

type ExhibitFormProps = {
  exhibit?: Exhibit | null
  onSubmit: (values: ExhibitData, helpers: FormikHelpers<ExhibitData>) => any
  previewing: boolean
  closePreview: () => any
}

// Helpers ----

const exhibitInformationData = (information: ExhibitInformation): ExhibitInformationData => ({
  name: information.name,
  content: information.content,
  visibility: information.visibility
})

const elementData = (element: Element): ElementData => ({
  id: element.id,
  name: element.name,
  caption: element.caption,
  description: element.description,
  link: element.link ? {text: element.link.text, url: element.link.url} : null,
  is_featured: element.is_featured,
  media: element.media.map(file => ({id: file.id})),
  facts: element.facts.map(fact => ({
    id: fact.id,
    type: fact.type ?? FactType.SHORT_TEXT,
    icon: fact.icon ?? IconName.QUESTION_MARK_CIRCLE,
    name: fact.name ?? '',
    value: fact.value ?? '',
  })),
  information: element.information.map(info => ({
    id: info.id,
    name: info.name,
    content: info.content,
    order: info.order
  }))
})

// TODO: Make this reusable
function collectionAsItem(collection: Collection) {
  return {
    id: collection.id,
    name: collection.name
  }
}

// TODO: Make this reusable
function exhibitTypeAsItem(exhibitType: ExhibitType) {
  return {
    id: exhibitType.id || 0,
    name: exhibitType.name,
  }
}

// TODO: Make this reusable
function buildingAsItem(building: Building) {
  return {
    id: building.id || 0,
    name: building.name,
    icon: IconName.OFFICE_BUILDING
  }
}

// TODO: Make this reusable
function floorAsItem(floor: Floor) {
  return {
    id: floor.id || 0,
    name: floor.name,
    description: floor.building.name,
    icon: IconName.CHEVRON_UP_DOWN
  }
}

// TODO: Make this reusable
function zoneAsItem(zone: Zone) {
  return {
    id: zone.id || 0,
    name: zone.name,
    description: zone.site.name,
    icon: IconName.CUBE_TRANSPARENT
  }
}

function elementMedia(exhibit?: Exhibit | null, elementId?: number | null): Array<MediaFile> {
  return exhibit?.elements.find(element => element.id === elementId)?.media || []
}

function streamData(stream: Stream): StreamData {
  return {
    provider: stream.provider,
    reference: stream.reference,
    meta: stream.meta
  }
}

// Component ----

function ExhibitForm({closePreview, exhibit, onSubmit, previewing}: ExhibitFormProps) {
  const {termFor, subdomain} = useOperator();
  const auth = useAuth();
  const linkOptions = useLinkOptions('exhibit', [LinkOptions.MEMBERS_ONLY]);

  const [selectedExhibitType, setSelectedExhibitType] = useState<ExhibitType | null>(exhibit?.type || null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | undefined>(exhibit?.location?.building?.id);
  const [selectedFloorId, setSelectedFloorId] = useState<number | undefined>(exhibit?.location?.id);
  const [uploadedMedia, setUploadedMedia] = useState<Array<AnyMedia>>(exhibit?.media || []);
  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();

  const {data: exhibitTypes, isLoading: typesLoading} = useQuery(
    ['exhibit-types.all', {page: 1}],
    () => ngage.exhibitTypes.all(1),
    {enabled: auth.can('exhibit-types:read')}
  )

  const {data: collections, isLoading: collectionsLoading} = useQuery(
    ['collections.all', {page: 1}],
    () => ngage.collections.all({page: 1}),
    {enabled: auth.can('collections:read')}
  )

  const {data: zones, isLoading: zonesLoading} = useQuery(
    ['zones.all', {page: 1, floors: selectedFloorId}],
    () => ngage.zones.all({page: 1, floors: selectedFloorId}),
    {enabled: auth.can('zones:read')}
  )

  const {data: buildings, isLoading: buildingsLoading} = useQuery(
    ['buildings.filtered'],
    () => ngage.buildings.filtered(),
    {enabled: auth.can('buildings:read')}
  )

  const {data: floors, isLoading: floorsLoading} = useQuery(
    ['floors.filtered', { building: selectedBuildingId}],
    () => ngage.floors.filtered({ building: selectedBuildingId}),
    {enabled: auth.can('floors:read')}
  )

  const createAIContent = useMutation((data: ExhibitAIContentAPIData) => ngage.aiContent.create(data), {
    onError: error => {
      console.error('Error creating AI content:', error);
    }
  });

  async function selectExhibitType(typeId: number, {setFieldValue, values}: FormikProps<ExhibitData>) {
    const exhibitType = await ngage.exhibitTypes.get(typeId)

    if (!exhibitType) return

    const templatedFacts = exhibitType.fact_templates?.map(template => values.facts?.find(fact => fact.template_id === template.id) ?? {
      template_id: template.id,
      value: ''
    }) ?? []
    const extraFacts = values.facts?.filter(fact => !fact.template_id) ?? []

    void setFieldValue('type_id', typeId)
    void setFieldValue('facts', [...templatedFacts, ...extraFacts])

    setSelectedExhibitType(exhibitType)
  }

  function toggleStream(hasStream: boolean, {setFieldValue}: FormikProps<ExhibitData>) {
    void setFieldValue('stream', hasStream ? {provider: '', reference: ''} : null);
  }

  function toggleIndoor(isIndoor: boolean, {setFieldValue}: FormikProps<ExhibitData>) {
    void setFieldValue('is_indoor', isIndoor);
    void setFieldValue('position', null);
    void setFieldValue('zone_id', null);

    if (!isIndoor) {
      void setFieldValue('location_id', null);
      void setFieldValue('location_type', null);
      void setFieldValue('building_id', null);

      setSelectedBuildingId(undefined);
      setSelectedFloorId(undefined);
    }
  }

  function selectBuilding(buildingId: number, formik: FormikHelpers<BuildingData>) {
    setSelectedBuildingId(buildingId);
    void formik.setFieldValue('building_id', buildingId).then(_ => {
      void formik.validateField('building_id');
      void formik.validateField('location_id');
    });
    void formik.setFieldValue('position', null);
  }

  function selectFloor(floorId: number, formik: FormikHelpers<FloorData>) {
    setSelectedFloorId(floorId);
    void formik.setFieldValue('location_type', 'floor');
    void formik.setFieldValue('location_id', floorId).then(_ => {
      void formik.validateField('location_id')
    });
    void formik.setFieldValue('position', null);
  }

  function selectZone(zoneId: number, formik: FormikHelpers<ExhibitData>) {
    void formik.setFieldValue('zone_id', zoneId).then(_ => {
      void formik.validateField('zone_id')
    });
    void formik.setFieldValue('position', null);

    // Find the zone and fly to it on the map
    const zone = zones?.data.find(zone => zone.id === Number(zoneId));
    setMapFlyToGeometry(zone?.area || exhibit?.position);
  }

  function handleUploadedFiles(files: Array<MediaFile>, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('media', files.map(file => ({id: file.id, visibility: Visibility.EVERYWHERE})));
    setUploadedMedia(files as Array<AnyMedia>);
  }

  function handleAIContent(formik: FormikProps<ExhibitData>) {
      createAIContent.mutateAsync({
        content_type: 'exhibit',
        subject: formik.values.name ?? '',
        exhibit_type: selectedExhibitType?.id ?? 0,
      }).then(aiContent => {
        // TODO: Remove this once the AI response no longer intemittently returns an array
        // Defensive logic to prevent app crashing if `content` property is an empty array from the AI response
        if (Array.isArray(aiContent.content)) return;

        // Set form fields data
        setAIContentFormFields(aiContent, formik);
      }).catch((error) => null); // Error handled in useMutation onError callback
  }

  function setAIContentFormFields(data: AIContent, formik: FormikProps<ExhibitData>) {
    if (!data || !data.content || data.content_type !== 'exhibit') return;

    formik.setFieldValue('caption', data.content.caption).catch(console.error);
    formik.setFieldValue('description', data.content.description).catch(console.error);
    formik.setFieldValue('facts', data.content.facts).catch(console.error);
    formik.setFieldValue('information', data.content.information).catch(console.error);
  }

  const canUseBuildings = auth.canUse(FeatureFlag.IndoorMapping);

  const initialFactValues: Array<FactData> = exhibit?.facts.map(fact => ({
    id: fact.id,
    template_id: fact.template?.id,
    type: fact.type ?? FactType.SHORT_TEXT,
    icon: fact.icon ?? IconName.QUESTION_MARK_CIRCLE,
    name: fact.name ?? '',
    value: fact.value ?? '',
  })) ?? []

  const validationSchema = Yup.object().shape({
    type_id: Yup.number().required('This field is required'),
    name: Yup.string().required("This field is required").max(255, "Maximum of 255 characters"),
    caption: Yup.string().max(50, "Maximum of 50 characters"),
    description: Yup.string().max(255, "Maximum character limit exceeded"),
    zone_id: Yup.number().required('This field is required'),
    building_id:
      Yup.number().nullable().when('is_indoor', ([isIndoor]: any, schema: any) => {
        if (!isIndoor) return schema;
        return schema.required('This field is required');
      }),
    location_id:
      Yup.number().nullable().when(['is_indoor', 'building_id'], ([isIndoor, buildingId]: any, schema: any) => {
        if (!isIndoor) return schema;
        return schema.required(!!buildingId ? 'This field is required' : 'Please select a building first');
      }),
    link: Yup.object().shape({
      text: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
      url: Yup.string()
        .required('This field is required')
        .max(250, 'Maximum 250 characters')
        .test('valid-url', urlTest),
      options: Yup.array().of(Yup.string()),
    }).nullable(),
    facts: Yup.array().of(Yup.object().shape({
      value: Yup.string().required('This field is required'),
    })),
    information: Yup.array().of(Yup.object().shape({
      name: Yup.string().required('This field is required').max(255, 'Maximum of 255 characters'),
      content: Yup.string().required('This field is required').max(50000, 'Maximum character limit exceeded'),
    })),
    stream: Yup.object().shape({
      provider: Yup.string().required('This field is required'),
      reference: Yup.string().required('This field is required').max(250, 'Maximum of 255 characters'),
    }).nullable(),
    sponsorship_information: Yup.string().max(20000, 'Maximum character limit exceeded'),
    sponsorship_link: Yup.object().shape({
      text: Yup.string().required('This field is required'),
      url: Yup.string().required('This field is required'),
    }).nullable(),
    element_introduction: Yup.string().max(250, 'Maximum of 250 characters'),
    elements: Yup.array().of(Yup.object().shape({
      name: Yup.string().required('This field is required').max(250, 'Maximum of 250 characters'),
      caption: Yup.string().nullable().max(50, 'Maximum of 50 characters'),
      description: Yup.string().nullable().max(250, 'Maximum character limit exceeded'),
      link: Yup.object().shape({
        text: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
        url: Yup.string()
          .required('This field is required')
          .max(250, 'Maximum of 250 characters')
          .test('valid-url', urlTest),
      }).nullable(),
      facts: Yup.array().of(Yup.object().shape({
        type: Yup.string().required('This field is required'),
        icon: Yup.string().required('This field is required'),
        name: Yup.string().required('This field is required').max(250, 'Maximum of 250 characters'),
        value: Yup.string().required('This field is required'),
      })),
      information: Yup.array().of(Yup.object().shape({
        name: Yup.string().required('This field is required').max(255, 'Maximum of 255 characters'),
        content: Yup.string().required('This field is required').max(50000, 'Maximum character limit exceeded'),
      })),
    })),
    position: Yup.object().required(`Please place the ${termFor('exhibit')} on the map using the drawing tool in the top left corner`),
  })

  const initialValues: ExhibitData = {
    type_id: exhibit?.type.id,
    name: exhibit?.name || '',
    caption: exhibit?.caption || '',
    description: exhibit?.description || '',
    link: exhibit?.link ? {text: exhibit.link.text, url: exhibit.link.url, options: exhibit.link.options} : null,
    accessibility_criteria: exhibit?.accessibility_criteria || [],
    is_sustainable: exhibit?.is_sustainable ?? false,
    is_bookable: exhibit?.is_bookable ?? false,
    is_destination: exhibit?.is_destination ?? true,
    element_introduction: exhibit?.element_introduction ?? '',
    sponsorship_information: exhibit?.sponsorship_information || '',
    sponsorship_link: exhibit?.sponsorship_link ? {
      text: exhibit.sponsorship_link.text,
      url: exhibit.sponsorship_link.url
    } : null,
    facts: initialFactValues,
    information: exhibit?.information.map(exhibitInformationData) || [],
    media: exhibit?.media.map(file => ({id: file.id, visibility: Visibility.EVERYWHERE})) || [],
    stream: exhibit?.stream ? streamData(exhibit.stream) : null,
    elements: exhibit?.elements.map(elementData) || [],
    zone_id: exhibit?.zone.id,
    position: exhibit?.position,
    is_mapped: exhibit?.is_mapped ?? true,
    is_pinned: exhibit?.is_pinned ?? true,
    is_hidden: exhibit?.is_hidden ?? false,
    is_published: exhibit?.is_published ?? true,
    collection_ids: exhibit?.collections.map(collection => collection.id) || [],
    location_type: exhibit?.location?.type,
    location_id: exhibit?.location?.id,

    // Not required in exhibit payload
    building_id: exhibit?.location?.building?.id,
    is_indoor: exhibit?.location?.type === 'building' || exhibit?.location?.type === 'floor' || exhibit?.location?.type === 'room',
  }

  const bookableOperators: OperatorSubdomain[] = ['dev', 'test-operator', 'lhp'];

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">

                  {/* Key Information */}
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('exhibit')}
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <Selector
                          label={`${termFor('Exhibit Type')} *`}
                          isLoading={typesLoading}
                          items={exhibitTypes?.data.map(exhibitTypeAsItem) || []}
                          selectedId={initialValues.type_id}
                          onChange={selected => selectExhibitType(Number(selected.id), formik)}
                        />

                        {(!!formik.errors.type_id && formik.touched.type_id) &&
                          <FieldHelp isError>
                            {formik.errors.type_id}
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-3 sm:col-start-1">
                        <Field
                          type="text"
                          label="Name *"
                          hint="255 chars max"
                          name="name"
                          id="name"
                          autoComplete="off"
                          maxLength="255"
                          autoFocus
                        />
                      </div>
                    </div>

                    {!exhibit &&
                      <div className="pt-8 pb-5">
                        <GageGenerateContent
                          onClick={() => handleAIContent(formik)}
                          loading={createAIContent.isLoading}
                          disabled={!selectedExhibitType || !formik.values.name}
                          status={getAIContentStatus(createAIContent)}
                          contentFields={['Caption', 'Short Description', 'Facts', 'Information']}
                        />
                      </div>
                    }

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Field
                          type="text"
                          label="Caption"
                          hint="Optional. 50 chars max."
                          name="caption"
                          id="caption"
                          autoComplete="off"
                          maxLength="50"
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <Field
                          type="text"
                          label="Short Description"
                          hint="Optional. 250 chars max."
                          name="description"
                          id="description"
                          autoComplete="off"
                          maxLength="255"
                        />
                      </div>

                      {/* Accessibility */}
                      <div className="sm:col-span-6">
                        <AccessibilitySelector
                          selectedIds={initialValues.accessibility_criteria}
                          onChange={criteria => formik.setFieldValue('accessibility_criteria', criteria)}
                        />
                      </div>

                      {/* Sustainability */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Sustainable"
                          description={`Whether this ${termFor('exhibit')} is environmentally and socially sustainable`}
                          checked={formik.values.is_sustainable}
                          onChange={checked => formik.setFieldValue('is_sustainable', checked)}
                        />
                      </div>

                      {/* Booking */}
                      {(subdomain && bookableOperators.includes(subdomain)) &&
                        <div className="sm:col-span-6">
                          <Toggle
                            label="Bookable"
                            description={`Whether this ${termFor('exhibit')} can be booked`}
                            checked={formik.values.is_bookable}
                            onChange={checked => formik.setFieldValue('is_bookable', checked)}
                          />
                        </div>
                      }

                      {/* Collections */}
                      <div className="sm:col-span-4">
                        <MultiSelector
                          label={termFor('Collections')}
                          isLoading={collectionsLoading}
                          items={collections?.data.map(collectionAsItem) || []}
                          selectedIds={initialValues.collection_ids}
                          onChange={items => formik.setFieldValue('collection_ids', items?.map(item => item.id) || [])}
                        />
                      </div>

                      {/* Link */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Link"
                          checked={!!formik.values.link}
                          onChange={checked => formik.setFieldValue('link', checked ? {text: '', url: ''} : null)}
                        />

                        {!!formik.values.link &&
                          <div className="mt-2">
                            <LinkEditor
                              name="link"
                              placeholderText="Find out more"
                              placeholderUrl="https://www.n-gage.io"
                              options={linkOptions}
                            />
                          </div>
                        }
                      </div>
                    </div>
                  </div>

                  {/* Facts */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {termFor('Facts')}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Provide simple {termFor('facts')} about the {termFor('exhibit')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 lg:grid-cols-6">

                      {/* Exhibit Type not selected */}
                      {!selectedExhibitType &&
                        <div className="text-center sm:col-span-6">
                          <InformationCircleIcon className="mx-auto h-10 w-10 text-gray-400"/>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {termFor('Exhibit Type')} not selected
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Get started by selecting an {termFor('exhibit type')}
                          </p>
                        </div>
                      }

                      {/* Exhibit Type selected */}
                      {!!selectedExhibitType &&
                        <FactEditor name="facts" factTemplates={selectedExhibitType?.fact_templates}/>
                      }
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The main sections of information about this {termFor('exhibit')}
                      </p>
                    </div>
                    <div className="mt-6">
                      <InformationListField name="information" information={formik.values.information}/>
                    </div>
                  </div>

                  {/* Media */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Media
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Images, audio and video that's relevant to this {termFor('exhibit')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Files */}
                      <div className="sm:col-span-6">
                        <Uploader
                          type={[Uploader.types.IMAGE, Uploader.types.AUDIO, Uploader.types.VIDEO]}
                          files={exhibit?.media || []}
                          onChange={files => handleUploadedFiles(files, formik)}
                        />
                      </div>

                      {/* Stream */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Stream"
                          checked={!!formik.values.stream}
                          onChange={checked => toggleStream(checked, formik)}
                        />

                        {!!formik.values.stream &&
                          <fieldset className="mt-2 sm:flex bg-gray-50 p-4 rounded-md">
                            <div className="sm:w-1/3">
                              <SelectField
                                name={"stream.provider"}
                                label="Provider *"
                                options={Object.entries(StreamProvider).map(([, value]) => ({
                                  id: value,
                                  name: toTitleCase(value)
                                }))}
                              />
                            </div>

                            <div className="mt-6 sm:mt-0 sm:ml-4 sm:w-2/3">
                              <TextField
                                name={"stream.reference"}
                                label={formik.values.stream.provider === 'zoolife' ? 'Habitat ID *' : 'Reference *'}
                                hint='250 chars max'
                                maxLength={250}
                              />
                            </div>
                          </fieldset>
                        }
                      </div>
                    </div>
                  </div>

                  {/* Elements */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Elements
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Significant parts of this {termFor('exhibit')} that have their own information
                      </p>
                    </div>
                    <div className="mt-6">
                      <div>
                        <Field
                          type="text"
                          label="Introduction"
                          hint="Optional. 250 chars max."
                          name="element_introduction"
                          id="element_introduction"
                          autoComplete="off"
                          maxLength="250"
                        />
                      </div>

                      <div className="mt-2">
                        <FieldArray name="elements">
                          {(helpers: FieldArrayRenderProps) => (
                            <div>
                              <ul>
                                {formik.values.elements?.map((element, index) => (
                                  <li key={element.id ? `element-${element.id}` : `new-element-${index}`}
                                      className="my-4">
                                    <ElementForm
                                      element={element}
                                      media={elementMedia(exhibit, element.id)}
                                      index={index}
                                      helpers={helpers}
                                    />
                                  </li>
                                ))}
                              </ul>

                              <div className='mt-4'>
                                <Button
                                  color={Button.colors.SECONDARY}
                                  icon={PlusIcon}
                                  size={Button.sizes.X_SMALL}
                                  onClick={() => helpers.push({
                                    name: '',
                                    caption: null,
                                    description: null,
                                    media: [],
                                    facts: [],
                                    information: [],
                                    link: null,
                                  })}
                                >
                                  Add Element
                                </Button>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Location
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set where the {termFor('exhibit')} is located
                      </p>
                    </div>

                    {canUseBuildings &&
                      <>
                        <div className="mt-7">
                          <Toggle
                            label="Indoor"
                            description={`This ${termFor('exhibit')} is inside a building`}
                            multiline
                            checked={formik.values.is_indoor}
                            onChange={enabled => toggleIndoor(enabled, formik)}
                          />
                        </div>

                        {formik.values.is_indoor &&
                          <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 sm:col-span-6">
                              <div className="sm:col-span-3">
                                <Selector
                                  label={`${toTitleCase(termFor('Building'))} *`}
                                  isLoading={buildingsLoading}
                                  items={buildings?.data.map(buildingAsItem) || []}
                                  selectedId={formik.values.building_id}
                                  onChange={building => selectBuilding(building.id, formik)}
                                  disabled={!buildings}
                                />

                                {(!!formik.errors.building_id && formik.touched.building_id) &&
                                  <FieldHelp isError>
                                    {formik.errors.building_id}
                                  </FieldHelp>
                                }
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 sm:col-span-6">
                              <div className="sm:col-span-3">
                                <Selector
                                  label={`${toTitleCase(termFor('Floor'))} *`}
                                  isLoading={floorsLoading}
                                  items={floors?.data.map(floorAsItem) || []}
                                  selectedId={formik.values.location_id}
                                  onChange={floor => selectFloor(floor.id, formik)}
                                  disabled={!formik.values.building_id}
                                />

                                {(!!formik.errors.location_id && formik.touched.location_id) &&
                                  <FieldHelp isError>
                                    {formik.errors.location_id}
                                  </FieldHelp>
                                }
                              </div>
                            </div>
                          </div>
                        }
                      </>
                    }

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Selector
                          label={`${toTitleCase(termFor('Zone'))} *`}
                          isLoading={zonesLoading}
                          items={zones?.data.map(zoneAsItem) || []}
                          selectedId={initialValues.zone_id}
                          onChange={zone => selectZone(Number(zone.id), formik)}
                          disabled={formik.values.is_indoor && !formik.values.location_id}
                        />

                        {(!!formik.errors.zone_id && formik.touched.zone_id) &&
                          <FieldHelp isError>
                            {formik.errors.zone_id}
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-6 rounded-md overflow-hidden">
                        <span className="block text-sm font-medium text-gray-700">
                            Position
                        </span>
                        <div className="mt-1">
                          <Locator
                            height={'32rem'}
                            type='position'
                            geometry={initialValues.position}
                            flyToGeometry={mapFlyToGeometry}
                            onChange={geometry => formik.setFieldValue('position', geometry)}
                            buildingId={formik.values.building_id}
                            floorId={formik.values.location_id}
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Place the {termFor('exhibit')} on the map using the drawing tool
                        </p>

                        {(!!formik.errors.position && formik.touched.position) &&
                          <FieldHelp isError>
                            <div className="flex items-center gap-2">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                              {formik.errors.position}
                            </div>
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-3">
                        <Toggle
                          label="Show position"
                          description={`Show this ${termFor('exhibit')} on the map`}
                          multiline
                          checked={formik.values.is_mapped}
                          onChange={checked => {
                            void formik.setFieldValue('is_mapped', checked)
                            void formik.setFieldValue('is_pinned', checked);
                            void formik.setFieldValue('is_destination', checked);
                          }}
                        />
                      </div>

                      {formik.values.is_mapped &&
                        <>
                          <div className="sm:col-span-3">
                            <Toggle
                              label="Show pin"
                              description={`Show a pin for this ${termFor('exhibit')} on the map`}
                              multiline
                              checked={formik.values.is_pinned}
                              onChange={checked => formik.setFieldValue('is_pinned', checked)}
                            />
                          </div>

                          <div className="sm:col-span-3">
                            <Toggle
                              label="Directions"
                              description={`Allow visitors to request directions to this ${termFor('exhibit')}`}
                              multiline
                              checked={formik.values.is_destination}
                              onChange={checked => formik.setFieldValue('is_destination', checked)}
                            />
                          </div>
                        </>
                      }
                    </div>
                  </div>

                  {/* Sponsorship */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Sponsorship
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Information about {termFor('sponsorships')} for this {termFor('exhibit')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Information */}
                      <div className="sm:col-span-6">
                        <TextAreaField
                          rows={5}
                          label="Sponsorship Information"
                          hint="Optional. 20,000 chars max."
                          name="sponsorship_information"
                          id="sponsorship_information"
                          autoComplete="off"
                          maxChars={20000}
                        />
                      </div>

                      {/* Link */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Link"
                          checked={!!formik.values.sponsorship_link}
                          onChange={checked => formik.setFieldValue('sponsorship_link', checked ? {
                            text: '',
                            url: ''
                          } : null)}
                        />

                        {!!formik.values.sponsorship_link &&
                          <div className="mt-2">
                            <LinkEditor
                              name="sponsorship_link"
                              placeholderText="Sponsor me!"
                              placeholderUrl="https://www.n-gage.io"
                            />
                          </div>
                        }
                      </div>
                    </div>
                  </div>

                  {/* Visibility */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Visibility
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set the visibility of this {termFor('exhibit')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      <div className="sm:col-span-6">
                        <Toggle
                          label="Publish"
                          description={`Makes this ${termFor('exhibit')} appear in the audience app, and makes it selectable in other parts of the operator app`}
                          checked={formik.values.is_published}
                          onChange={checked => formik.setFieldValue('is_published', checked)}
                          multiline
                        />
                      </div>

                      {formik.values.is_published &&
                        <div className="sm:col-span-6">
                          <Toggle
                            label="Hide from Listings"
                            description={`Hides this ${termFor('exhibit')} from any listings in the audience app, such as "Explore"`}
                            checked={formik.values.is_hidden}
                            onChange={checked => formik.setFieldValue('is_hidden', checked)}
                            multiline
                          />
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>

            {previewing &&
              <ExhibitPreview
                exhibitId={exhibit?.id}
                exhibit={formik.values}
                exhibitType={selectedExhibitType ?? null}
                exhibitMedia={uploadedMedia}
                onClose={closePreview}
              />
            }
          </Form>
        )
      }}
    </Formik>
  );
}

// Export ----

export default ExhibitForm;
